import { supabase } from '../../../lib/supabaseClient';
import { sha256 } from '../../../lib/hashUtils';
import { fetchBlueprintExecutionMetrics, ensureQualityAlertsForBlueprints, type BlueprintExecutionMetrics, BLUEPRINT_QUALITY_ALERT_THRESHOLD } from '../../../services/blueprintPerformanceService';
import type { BlueprintSourceType, BlueprintStage, ProcessBlueprint, ProcessBlueprintVersion } from '../../../types/processBlueprint';
import {
  BLUEPRINTS_TABLE,
  BLUEPRINT_WITH_LATEST_VERSION_SELECT,
  mapBlueprintRow,
  scoreVersion,
} from './blueprintDataHelpers';

export interface BlueprintListFilters {
  organizationId: string;
  page?: number;
  pageSize?: number;
  status?: 'all' | 'draft' | 'published' | 'archived' | 'standard';
  category?: string | 'all';
  search?: string;
  sortKey?: 'name' | 'category' | 'status' | 'semver' | 'updated_at' | 'usage';
  sortDir?: 'asc' | 'desc';
}

export interface BlueprintListItem extends ProcessBlueprint {
  usage_count?: number;
  latestVersionSemver?: ProcessBlueprintVersion['versionSemver'];
  has_newer_version?: boolean;
  performanceMetrics?: BlueprintExecutionMetrics;
  createdByName?: string;
}

export interface BlueprintListResult {
  data: BlueprintListItem[];
  total: number;
  page: number;
  pageSize: number;
}

interface CreateBlueprintInput {
  organizationId: string;
  name: string;
  category?: string;
  description?: string;
  projectId?: string | null;
  sourceType?: BlueprintSourceType;
  sourceId?: string | null;
  stages?: ProcessBlueprint['stages'];
  status?: ProcessBlueprint['status'];
  createdBy?: string | null;
}

const baseSelect = BLUEPRINT_WITH_LATEST_VERSION_SELECT;

export async function listBlueprints(filters: BlueprintListFilters): Promise<BlueprintListResult> {
  const {
    organizationId,
    page = 1,
    pageSize = 10,
    status = 'all',
    category = 'all',
    search = '',
    sortKey = 'updated_at',
    sortDir = 'desc',
  } = filters;

  const from = (page - 1) * pageSize;
  const to = from + pageSize - 1;

  let query = supabase
    .from(BLUEPRINTS_TABLE)
    .select(baseSelect, { count: 'exact' })
    .eq('organization_id', organizationId);

  if (status !== 'all') {
    query = query.eq('status', status);
  }

  if (category !== 'all') {
    query = query.eq('category', category);
  }

  if (search) {
    query = query.ilike('name', `%${search}%`);
  }

  const ascending = sortDir === 'asc';
  if (sortKey !== 'semver' && sortKey !== 'usage') {
    query = query.order(sortKey, { ascending });
  } else {
    query = query.order('updated_at', { ascending: false });
  }

  const { data, count, error } = await query.range(from, to);
  if (error) throw error;

  const blueprintRows = data ?? [];
  const mappedBlueprints = blueprintRows.map((row) => mapBlueprintRow(row));
  const blueprintIds = mappedBlueprints.map((item) => item.id).filter(Boolean);

  let usageMap = new Map<string, number>();
  let versionMap = new Map<string, { semver: string; major: number; minor: number; patch: number }>();
  let userNameMap = new Map<string, string>();
  let metricsMap = new Map<string, BlueprintExecutionMetrics>();

  if (blueprintIds.length > 0) {
    // Fetch usage, newest versions, children, metrics, and user names in parallel
    // All sub-queries are defensive — tables may not exist yet
    const [usageResult, versionResult, childResult, userResult] = await Promise.all([
      // Usage counts
      supabase
        .from('process_blueprint_usage')
        .select('blueprint_id')
        .eq('organization_id', organizationId)
        .in('blueprint_id', blueprintIds)
        .then((res) => res, () => ({ data: null, error: null } as { data: any; error: any })),
      // Latest version per blueprint (ordered desc, deduplicated in JS)
      supabase
        .from('process_blueprint_versions')
        .select('blueprint_id, version_semver, version_major, version_minor, version_patch, created_at')
        .eq('organization_id', organizationId)
        .in('blueprint_id', blueprintIds)
        .order('created_at', { ascending: false })
        .then((res) => res, () => ({ data: null, error: null } as { data: any; error: any })),
      // Child blueprints (newer versions)
      supabase
        .from(BLUEPRINTS_TABLE)
        .select('parent_blueprint_id')
        .eq('organization_id', organizationId)
        .in('parent_blueprint_id', blueprintIds)
        .not('parent_blueprint_id', 'is', null),
      // User names from created_by UUIDs
      (() => {
        const creatorIds = [...new Set(mappedBlueprints.map(b => b.createdBy).filter(Boolean))] as string[];
        if (creatorIds.length === 0) return Promise.resolve({ data: null, error: null });
        return supabase
          .from('user_profiles')
          .select('*')
          .in('user_id', creatorIds);
      })(),
    ]);

    // Process usage
    if (!usageResult.error && usageResult.data) {
      usageResult.data.forEach((row: any) => {
        const id = row.blueprint_id as string;
        usageMap.set(id, (usageMap.get(id) ?? 0) + 1);
      });
    }

    // Process versions — keep only the latest per blueprint_id
    if (!versionResult.error && versionResult.data) {
      for (const row of versionResult.data) {
        const bpId = row.blueprint_id as string;
        if (!versionMap.has(bpId)) {
          versionMap.set(bpId, {
            semver: row.version_semver ?? `${row.version_major ?? 1}.${row.version_minor ?? 0}.${row.version_patch ?? 0}`,
            major: row.version_major ?? 1,
            minor: row.version_minor ?? 0,
            patch: row.version_patch ?? 0,
          });
        }
      }
    }

    // Process children
    let newerSet = new Set<string>();
    if (!childResult.error) {
      childResult.data?.forEach((row) => {
        if (row.parent_blueprint_id) newerSet.add(row.parent_blueprint_id);
      });
    }

    // Process user names
    if (!userResult.error && userResult.data) {
      for (const row of userResult.data as any[]) {
        userNameMap.set(row.user_id, row.full_name || row.name || row.email || row.user_id);
      }
    }

    // If user_profiles query failed, try user_organizations or auth.users metadata
    if (userResult.error) {
      console.warn('[blueprint.service] user_profiles query failed, trying fallback:', userResult.error.message);
      const creatorIds = [...new Set(mappedBlueprints.map(b => b.createdBy).filter(Boolean))] as string[];
      if (creatorIds.length > 0) {
        const { data: orgUsers } = await supabase
          .from('user_organizations')
          .select('user_id, users:user_id(email, raw_user_meta_data)')
          .in('user_id', creatorIds);
        if (orgUsers) {
          for (const row of orgUsers as any[]) {
            const name = row.users?.raw_user_meta_data?.name || row.users?.email || row.user_id;
            if (row.user_id && name) userNameMap.set(row.user_id, name);
          }
        }
      }
    }

    // Fetch metrics
    const metrics = await fetchBlueprintExecutionMetrics({ organizationId, blueprintIds });
    metricsMap = new Map(metrics.map((metric) => [metric.blueprintId, metric]));

    // fire-and-forget alert sync
    void ensureQualityAlertsForBlueprints({
      organizationId,
      items: metrics,
      threshold: BLUEPRINT_QUALITY_ALERT_THRESHOLD,
    }).catch((alertError) => {
      console.warn('[blueprint.service] quality alert sync failed', alertError);
    });

    // Build final items with enriched version + user name
    let items = mappedBlueprints.map((item) => {
      const latestVer = versionMap.get(item.id);
      return {
        ...item,
        versionSemver: latestVer?.semver ?? item.versionSemver,
        versionMajor: latestVer?.major ?? item.versionMajor,
        versionMinor: latestVer?.minor ?? item.versionMinor,
        versionPatch: latestVer?.patch ?? item.versionPatch,
        latestVersionSemver: latestVer?.semver ?? item.versionSemver,
        usage_count: usageMap.get(item.id) ?? 0,
        has_newer_version: newerSet.has(item.id),
        performanceMetrics: metricsMap.get(item.id),
        createdByName: item.createdBy ? (userNameMap.get(item.createdBy) ?? undefined) : undefined,
      };
    });

    if (sortKey === 'usage') {
      items = items.sort((a, b) => {
        const diff = (a.usage_count ?? 0) - (b.usage_count ?? 0);
        return sortDir === 'asc' ? diff : -diff;
      });
    } else if (sortKey === 'semver') {
      items = items.sort((a, b) => {
        const scoreA = scoreVersion({
          version_major: a.versionMajor,
          version_minor: a.versionMinor,
          version_patch: a.versionPatch,
        });
        const scoreB = scoreVersion({
          version_major: b.versionMajor,
          version_minor: b.versionMinor,
          version_patch: b.versionPatch,
        });
        const diff = scoreA - scoreB;
        return sortDir === 'asc' ? diff : -diff;
      });
    }

    return {
      data: items,
      total: count ?? 0,
      page,
      pageSize,
    };
  }

  // No blueprints — empty result
  return {
    data: [],
    total: count ?? 0,
    page,
    pageSize,
  };
}

export async function getBlueprintById(id: string, organizationId: string): Promise<ProcessBlueprint | null> {
  const { data, error } = await supabase
    .from(BLUEPRINTS_TABLE)
    .select(baseSelect)
    .eq('id', id)
    .eq('organization_id', organizationId)
    .maybeSingle();
  if (error) throw error;
  return data ? mapBlueprintRow(data) : null;
}

export async function publishBlueprint(id: string, organizationId: string): Promise<void> {
  const { error } = await supabase
    .from(BLUEPRINTS_TABLE)
    .update({ status: 'published' })
    .eq('id', id)
    .eq('organization_id', organizationId);
  if (error) throw error;
}

export async function archiveBlueprint(id: string, organizationId: string): Promise<void> {
  const { error } = await supabase
    .from(BLUEPRINTS_TABLE)
    .update({ status: 'archived' })
    .eq('id', id)
    .eq('organization_id', organizationId);
  if (error) throw error;
}

export async function cloneBlueprint(id: string, organizationId: string, name: string): Promise<ProcessBlueprint> {
  const { data: original, error: fetchError } = await supabase
    .from(BLUEPRINTS_TABLE)
    .select('*')
    .eq('id', id)
    .eq('organization_id', organizationId)
    .maybeSingle();
  if (fetchError) throw fetchError;
  if (!original) throw new Error('Blueprint not found');

  const { data, error } = await supabase
    .from(BLUEPRINTS_TABLE)
    .insert({
      organization_id: organizationId,
      name,
      category: original.category,
      description: original.description,
      payload: (original as any).payload ?? null,
      status: 'draft',
    })
    .select('id')
    .maybeSingle();
  if (error) throw error;
  if (!data) throw new Error('Falha ao clonar blueprint');

  const cloned = await getBlueprintById(data.id as string, organizationId);
  if (!cloned) throw new Error('Blueprint clonado não encontrado');
  return cloned;
}

export async function createBlueprint(payload: CreateBlueprintInput) {
  if (!payload.organizationId) throw new Error('organizationId é obrigatório');
  const { data, error } = await supabase.rpc('create_blueprint_with_version', {
    p_organization_id: payload.organizationId,
    p_name: payload.name,
    p_category: payload.category ?? 'geral',
    p_description: payload.description ?? null,
    p_created_by: payload.createdBy ?? null,
    p_version_major: 1,
    p_version_minor: 0,
    p_version_patch: 0,
  });

  if (error) throw error;
  if (!data) throw new Error('create_blueprint_with_version não retornou identificador');

  const blueprintId = typeof data === 'string' ? data : (data as { id?: string; blueprint_id?: string }).id ?? (data as { id?: string; blueprint_id?: string }).blueprint_id;
  if (!blueprintId) throw new Error('Identificador do blueprint não encontrado na resposta da RPC');

  const blueprint = await getBlueprintById(blueprintId, payload.organizationId);
  if (!blueprint) throw new Error('Blueprint recém-criado não encontrado');
  return blueprint;
}

export async function createVersion(
  blueprintId: string,
  organizationId: string,
  notes: string,
  bumpType: 'major' | 'minor' | 'patch' = 'patch',
): Promise<ProcessBlueprintVersion> {
  if (!blueprintId) throw new Error('blueprintId é obrigatório');
  if (!organizationId) throw new Error('organizationId é obrigatório');

  const { data, error } = await supabase.rpc('bump_blueprint_version', {
    p_blueprint_id: blueprintId,
    p_type: bumpType,
    p_change_summary: notes ?? null,
  });

  if (error) throw error;
  if (!data) throw new Error('bump_blueprint_version não retornou dados');

  const versionRow = data as Record<string, any>;

  // Compute and store snapshot_hash (column may not exist in older envs — fire-and-forget)
  if (versionRow.snapshot_json) {
    sha256(versionRow.snapshot_json)
      .then(hash =>
        supabase
          .from('process_blueprint_versions')
          .update({ snapshot_hash: hash })
          .eq('id', versionRow.id)
          .eq('organization_id', organizationId)
      )
      .catch(() => { /* non-fatal */ });
  }

  return {
    id: versionRow.id,
    blueprintId: versionRow.blueprint_id,
    organizationId: versionRow.organization_id,
    version: versionRow.version,
    versionSemver: versionRow.version_semver,
    versionMajor: versionRow.version_major,
    versionMinor: versionRow.version_minor,
    versionPatch: versionRow.version_patch,
    changeType: versionRow.change_type,
    snapshotJson: versionRow.snapshot_json ?? {},
    snapshotHash: versionRow.snapshot_hash ?? undefined,
    changeSummary: versionRow.change_summary ?? undefined,
    createdBy: versionRow.created_by ?? undefined,
    createdAt: versionRow.created_at,
  };
}

export async function markAsStandard(blueprintId: string, organizationId: string) {
  const { error } = await supabase.rpc('set_process_blueprint_as_standard', {
    p_blueprint_id: blueprintId,
    p_organization_id: organizationId,
  });
  if (error) throw error;
}

export async function updateBlueprint(payload: Partial<ProcessBlueprint> & { id: string; organization_id: string }) {
  const { id, organization_id, ...rest } = payload;
  const { error } = await supabase
    .from(BLUEPRINTS_TABLE)
    .update({ ...rest, updated_at: new Date().toISOString() })
    .eq('id', id)
    .eq('organization_id', organization_id);
  if (error) throw error;
}

/**
 * Atualiza as etapas de um blueprint.
 * Bloqueia edição se o blueprint estiver publicado ou for padrão corporativo.
 */
export async function updateBlueprintStages(
  blueprintId: string,
  organizationId: string,
  stages: BlueprintStage[],
): Promise<ProcessBlueprint> {
  // 1. Buscar blueprint atual para validar status
  const current = await getBlueprintById(blueprintId, organizationId);
  if (!current) throw new Error('Blueprint não encontrado');

  if (current.status === 'published' || current.status === 'standard') {
    throw new Error('Blueprint publicado ou padrão corporativo. Crie uma nova versão antes de editar.');
  }

  // 2. Atualizar stages no banco
  const { data, error } = await supabase
    .from(BLUEPRINTS_TABLE)
    .update({ stages, updated_at: new Date().toISOString() })
    .eq('id', blueprintId)
    .eq('organization_id', organizationId)
    .select(BLUEPRINT_WITH_LATEST_VERSION_SELECT)
    .single();

  if (error) throw error;
  return mapBlueprintRow(data);
}
